import { useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, Search, Flag, Tags, ScanSearch, Sparkles, GitMerge, ListChecks, Star, Languages, MoreHorizontal, X, MessageSquare, EyeOff } from "lucide-react";
import { HiddenBadge } from "@/components/EntityDetail";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useQuery } from "@tanstack/react-query";
import { api, errMsg, isPublicView } from "@/lib/api";
import { useEntities, useInvalidate } from "@/lib/queries";
import { allCategories, catColor, catLabel, FLAG_META } from "@/lib/lore";
import { CategoryBadge, PageHeader } from "@/components/CategoryBadge";
import { EntityFormDialog } from "@/components/EntityFormDialog";
import { CategoryManager } from "@/components/CategoryManager";
import { MeaningSearch } from "@/components/MeaningSearch";
import { MissingEntriesPanel } from "@/components/MissingEntriesPanel";
import { MergeDialog } from "@/components/MergeDialog";
import { BulkSendDialog } from "@/components/language/BulkSendDialog";
import { TemplateManager, WorldSettingsPicker } from "@/components/TemplateManager";
import { CompletenessBar } from "@/components/CompletenessPanel";
import { SafeImg } from "@/components/ImageGallery";
import { FilterPopover } from "@/components/FilterPopover";

const FlagDots = ({ flags }) => (
  <span className="flex items-center gap-1" data-testid="lore-card-flags">
    <Flag className="h-3.5 w-3.5 text-[#F59E0B]" />
    {Object.keys(FLAG_META).filter((t) => flags.some((f) => f.type === t)).map((t) => (
      <span key={t} className="h-1.5 w-1.5 rounded-full" style={{ background: FLAG_META[t].color }} title={FLAG_META[t].label} />
    ))}
  </span>
);

function FlagFilter({ value, onChange, entities }) {
  const count = (t) => entities.filter((e) => e.flags?.some((f) => !t || f.type === t)).length;
  const opts = [["", "All entries"], ["any", `Flagged ${count()}`], ...Object.entries(FLAG_META).map(([k, m]) => [k, `${m.label} ${count(k)}`])];
  return (
    <div className="hidden sm:flex sm:flex-wrap items-center gap-1.5 mb-6" data-testid="flag-filter">
      <Flag className="h-3.5 w-3.5 text-[#A8A090] mr-1" />
      {opts.map(([k, label]) => (
        <button key={k} onClick={() => onChange(k)} data-testid={`flag-filter-${k || "all"}`}
          className={`rounded-sm px-2.5 py-1 text-xs border transition-colors ${value === k ? "border-[#D97706] text-[#F59E0B] bg-[#D97706]/10" : "border-[#2D3442] text-[#A8A090] hover:text-[#F3EBDD]"}`}
          style={value === k && FLAG_META[k] ? { borderColor: FLAG_META[k].color, color: FLAG_META[k].color } : {}}>
          {label}
        </button>
      ))}
    </div>
  );
}

function InfoFilter({ value, onChange, entities }) {
  const n = (k) => entities.filter((e) => (k === "incomplete" ? e.completeness?.percent < 100 : e.completeness?.priority_count > 0)).length;
  const opts = [["", "Any completeness"], ["incomplete", `Incomplete ${n("incomplete")}`], ["priority", `Priority to-dos ${n("priority")}`]];
  return (
    <div className="-mt-3 mb-6 hidden sm:flex sm:flex-wrap items-center gap-1.5" data-testid="info-filter">
      <ListChecks className="mr-1 h-3.5 w-3.5 text-[#A8A090]" />
      {opts.map(([k, label]) => (
        <button key={k} onClick={() => onChange(k)} data-testid={`info-filter-${k || "all"}`}
          className={`rounded-sm border px-2.5 py-1 text-xs transition-colors ${value === k ? "border-[#D97706] bg-[#D97706]/10 text-[#F59E0B]" : "border-[#2D3442] text-[#A8A090] hover:text-[#F3EBDD]"}`}>{label}</button>
      ))}
    </div>
  );
}

const HiddenToggle = ({ on, set, count, testid, className = "" }) => (
  <button onClick={() => set(!on)} data-testid={testid} aria-pressed={on}
    className={`flex items-center gap-1.5 rounded-sm border px-2.5 py-1 text-xs transition-colors ${on ? "border-[#A78BFA] bg-[#A78BFA]/10 text-[#C4B5FD]" : "border-[#2D3442] text-[#A8A090] hover:text-[#F3EBDD]"} ${className}`}>
    <EyeOff className="h-3.5 w-3.5" /><span className={testid.startsWith("mobile") ? "sr-only" : ""}>Show hidden only</span> <span className="opacity-60">{count}</span>
  </button>
);

export default function LorePage() {
  const { universe, canEdit, base, isPublic, openEntity } = useOutletContext();
  const { data: cc = { open: {}, unread: {} } } = useQuery({ queryKey: ["comment-counts", universe.id], queryFn: () => api.get(`/universes/${universe.id}/comment-counts`).then((r) => r.data), enabled: !isPublicView() });
  const navigate = useNavigate();
  const invalidate = useInvalidate();
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [flagFilter, setFlagFilter] = useState("");
  const [creating, setCreating] = useState(false);
  const [managing, setManaging] = useState(false);
  const [merging, setMerging] = useState(false);
  const [sendingBulk, setSendingBulk] = useState(false);
  const [templating, setTemplating] = useState(false);
  const [infoFilter, setInfoFilter] = useState("");
  const [hiddenOnly, setHiddenOnly] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [mode, setMode] = useState("keyword");
  const { data: all = [] } = useEntities(universe.id);
  const { data: results } = useQuery({
    queryKey: ["entity-search", universe.id, q, all.length],
    queryFn: () => api.get(`/universes/${universe.id}/entities`, { params: { q } }).then((r) => r.data),
    enabled: q.length > 1,
  });
  const cats = allCategories();
  const counts = useMemo(() => Object.fromEntries(cats.map((c) => [c, all.filter((e) => e.category === c).length])), [all, cats]);
  const hiddenCount = all.filter((e) => e.hidden).length;
  const flagCount = (t) => all.filter((e) => e.flags?.some((f) => !t || f.type === t)).length;
  const list = (mode === "keyword" && q.length > 1 ? results || [] : all)
    .filter((e) => !cat || e.category === cat)
    .filter((e) => !flagFilter || e.flags?.some((f) => flagFilter === "any" || f.type === flagFilter))
    .filter((e) => !hiddenOnly || e.hidden)
    .filter((e) => !infoFilter || (infoFilter === "incomplete" ? e.completeness?.percent < 100 : e.completeness?.priority_count > 0));

  const review = async () => {
    setReviewing(true);
    try {
      const { data } = await api.post(`/universes/${universe.id}/ai/review`);
      invalidate(["entities", universe.id], "entity", ["suggestions", universe.id]);
      toast.success(`Review complete · ${data.flags_created} flags · ${data.missing_found} missing entries spotted`);
      if (data.flags_created) setFlagFilter("any");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setReviewing(false);
    }
  };

  return (
    <div className="px-4 sm:px-8 lg:px-12 py-10">
      <PageHeader eyebrow={`${all.length} ${all.length === 1 ? "entry" : "entries"}`} title="Lore Bible">
        {canEdit && (
          <>
            <div className="hidden sm:contents">
              <Button variant="ghost" onClick={() => setTemplating(true)} data-testid="manage-templates-button"><ListChecks className="h-4 w-4 mr-1.5" />Recommended info</Button>
              <Button variant="ghost" onClick={() => setManaging(true)} data-testid="manage-categories-button"><Tags className="h-4 w-4 mr-1.5" />Categories</Button>
              {universe.modules?.languages !== false && <Button variant="ghost" onClick={() => setSendingBulk(true)} disabled={!all.length} data-testid="bulk-send-to-language-button"><Languages className="h-4 w-4 mr-1.5" />Send to language</Button>}
              <Button variant="ghost" onClick={() => setMerging(true)} disabled={all.length < 2} data-testid="merge-entries-button"><GitMerge className="h-4 w-4 mr-1.5" />Merge</Button>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-10 w-10 sm:hidden" data-testid="lore-more-menu"><MoreHorizontal className="h-5 w-5" /></Button></DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onClick={() => setTemplating(true)} data-testid="lore-more-templates"><ListChecks className="h-4 w-4 mr-2" />Recommended info</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setManaging(true)} data-testid="lore-more-categories"><Tags className="h-4 w-4 mr-2" />Categories</DropdownMenuItem>
                {universe.modules?.languages !== false && <DropdownMenuItem disabled={!all.length} onClick={() => setSendingBulk(true)} data-testid="lore-more-send"><Languages className="h-4 w-4 mr-2" />Send to language</DropdownMenuItem>}
                <DropdownMenuItem disabled={all.length < 2} onClick={() => setMerging(true)} data-testid="lore-more-merge"><GitMerge className="h-4 w-4 mr-2" />Merge</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="outline" onClick={review} disabled={reviewing || !all.length} data-testid="review-lore-button">
              <ScanSearch className="h-4 w-4 mr-1.5" />{reviewing ? "Reviewing…" : "Review lore"}
            </Button>
            <Button onClick={() => setCreating(true)} data-testid="new-entity-button"><Plus className="h-4 w-4 mr-1" />New entry</Button>
          </>
        )}
      </PageHeader>
      {reviewing && <p className="-mt-4 mb-6 font-display text-xl text-[#E8DFD1] lf-caret" data-testid="review-progress">The continuity editor is reading every page</p>}
      {canEdit && !universe.settings_chosen && (
        <div className="mb-6 flex flex-wrap items-center gap-3 rounded-md border border-[#D97706]/30 bg-[#D97706]/5 px-4 py-3 text-sm" data-testid="world-settings-banner">
          <span className="text-[#E8DFD1]">What kind of world is this? It decides which recommended fields apply (e.g. Planet for sci-fi).</span>
          <WorldSettingsPicker uid={universe.id} compact />
          <button onClick={() => navigate(`${base}/settings`)} className="text-xs text-[#F59E0B] hover:underline" data-testid="world-settings-banner-more">More options in Universe settings</button>
        </div>
      )}
      {canEdit && <MissingEntriesPanel uid={universe.id} onOpen={openEntity} />}
      {!isPublic && (
        <div className="inline-flex mb-4 rounded-md border border-[#2D3442] bg-[#13171F] p-0.5" data-testid="search-mode-toggle">
          {[["keyword", "Keyword", Search], ["meaning", "By meaning", Sparkles]].map(([k, label, Icon]) => (
            <button key={k} onClick={() => setMode(k)} data-testid={`search-mode-${k}`}
              className={`flex items-center gap-1.5 rounded px-3 py-1.5 text-xs transition-colors ${mode === k ? "bg-[#1A202C] text-[#F3EBDD] shadow-[inset_0_-2px_0_#D97706]" : "text-[#A8A090] hover:text-[#F3EBDD]"}`}>
              <Icon className="h-3.5 w-3.5" />{label}
            </button>
          ))}
        </div>
      )}
      {mode === "meaning" && <MeaningSearch uid={universe.id} base={base} />}
      <div className="flex flex-wrap items-center gap-3 mb-8">
        {mode === "keyword" && (
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#A8A090]" />
            <Input className="pl-9" placeholder="Search names, summaries, notes…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="lore-search-input" />
          </div>
        )}
        <div className="hidden sm:flex sm:flex-wrap gap-1.5 max-w-full">
          <button onClick={() => setCat("")} data-testid="lore-filter-all"
            className={`rounded-sm px-3 py-1.5 text-xs transition-colors ${!cat ? "bg-[#D97706] text-[#0B0D10]" : "text-[#A8A090] hover:text-[#F3EBDD] border border-[#2D3442]"}`}>All</button>
          {cats.map((c) => (
            <button key={c} onClick={() => setCat(c === cat ? "" : c)} data-testid={`lore-filter-${c}`}
              className={`rounded-sm px-3 py-1.5 text-xs border transition-colors ${cat === c ? "text-[#0B0D10]" : "text-[#A8A090] hover:text-[#F3EBDD] border-[#2D3442]"}`}
              style={cat === c ? { background: catColor(c), borderColor: catColor(c) } : {}}>
              {catLabel(c)} <span className="opacity-60">{counts[c]}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="mb-6 flex gap-2 sm:hidden" data-testid="mobile-filter-bar">
        <FilterPopover id="mobile-category-filter" icon={Tags} title="Category" value={cat} onChange={setCat}
          options={[{ k: "", label: "All", count: all.length }, ...cats.map((c) => ({ k: c, label: catLabel(c), count: counts[c], color: catColor(c) }))]} />
        <FilterPopover id="mobile-flag-filter" icon={Flag} title="Flags" value={flagFilter} onChange={setFlagFilter}
          options={[{ k: "", label: "All entries" }, { k: "any", label: "Flagged", count: flagCount() }, ...Object.entries(FLAG_META).map(([k, m]) => ({ k, label: m.label, count: flagCount(k), color: m.color }))]} />
        <FilterPopover id="mobile-info-filter" icon={ListChecks} title="Completeness" value={infoFilter} onChange={setInfoFilter}
          options={[{ k: "", label: "Any completeness" }, { k: "incomplete", label: "Incomplete", count: all.filter((e) => e.completeness?.percent < 100).length }, { k: "priority", label: "Priority to-dos", count: all.filter((e) => e.completeness?.priority_count > 0).length }]} />
        {canEdit && <HiddenToggle on={hiddenOnly} set={setHiddenOnly} count={hiddenCount} testid="mobile-hidden-filter" className="h-11 shrink-0" />}
        {(cat || flagFilter || infoFilter || hiddenOnly) && (
          <button onClick={() => { setCat(""); setFlagFilter(""); setInfoFilter(""); setHiddenOnly(false); }} data-testid="mobile-clear-filters-button"
            className="flex h-11 shrink-0 items-center gap-1 rounded-md px-2 text-xs text-[#F59E0B] hover:bg-[#1A202C]"><X className="h-3.5 w-3.5" />Clear</button>
        )}
      </div>
      <FlagFilter value={flagFilter} onChange={setFlagFilter} entities={all} />
      <InfoFilter value={infoFilter} onChange={setInfoFilter} entities={all} />
      {canEdit && <div className="-mt-3 mb-6 hidden sm:flex"><HiddenToggle on={hiddenOnly} set={setHiddenOnly} count={hiddenCount} testid="hidden-filter" /></div>}
      {list.length === 0 ? (
        <div className="rounded-md border border-dashed border-[#2D3442] p-14 text-center" data-testid="lore-empty">
          <p className="font-display text-3xl">The pages are blank.</p>
          <p className="mt-2 text-sm text-[#A8A090]">Process a note with the AI scribe, or write an entry by hand.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="lore-grid">
          {list.map((e, i) => (
            <motion.button key={e.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i, 12) * 0.03 }}
              onClick={() => navigate(`${base}/lore/${e.id}`)} data-testid={`lore-card-${e.id}`}
              className="group text-left rounded-md border border-[#2D3442] bg-[#13171F] p-5 transition-[border-color,transform] duration-200 hover:-translate-y-0.5 hover:border-[#D97706]/50"
              style={{ borderLeft: `3px solid ${catColor(e.category)}` }}>
              {e.images?.[0] && <SafeImg src={e.images[0].url} alt={e.name} className="-mx-5 -mt-5 mb-4 h-36 w-[calc(100%+2.5rem)] rounded-t-md" testid={`lore-card-cover-${e.id}`} />}
              <div className="flex items-center justify-between"><CategoryBadge category={e.category} /><span className="flex items-center gap-2">{cc.open[e.id] > 0 && (
                <span className={`relative flex items-center gap-0.5 text-[10px] ${cc.unread[e.id] ? "text-[#F59E0B]" : "text-[#A8A090]"}`} title={`${cc.open[e.id]} open comment${cc.open[e.id] > 1 ? "s" : ""}${cc.unread[e.id] ? " · new replies" : ""}`} data-testid={`lore-card-comments-${e.id}`}>
                  <MessageSquare className="h-3 w-3" />{cc.open[e.id]}
                  {cc.unread[e.id] > 0 && <span className="absolute -right-1.5 -top-1 h-2 w-2 rounded-full bg-[#EF4444]" data-testid={`lore-card-unread-${e.id}`} />}
                </span>
              )}{e.hidden && <HiddenBadge testid={`lore-card-hidden-${e.id}`} />}{e.flags?.length > 0 && <FlagDots flags={e.flags} />}{e.is_stub && <span className="lf-label">stub</span>}</span></div>
              <div className="font-display text-2xl mt-3 text-[#F3EBDD] group-hover:text-[#F59E0B] transition-colors">{e.name}</div>
              <p className="mt-1.5 text-sm text-[#A8A090] line-clamp-2">{e.summary || "—"}</p>
              {e.completeness?.total > 0 && (
                <div className="mt-3 flex items-center gap-2" data-testid={`lore-card-completeness-${e.id}`}>
                  <CompletenessBar c={e.completeness} />
                  <span className="shrink-0 font-mono-lf text-[10px] text-[#A8A090]">{e.completeness.filled}/{e.completeness.total}</span>
                  {e.completeness.priority_count > 0 && <span className="flex shrink-0 items-center gap-0.5 text-[10px] text-[#F59E0B]"><Star className="h-3 w-3" fill="#F59E0B" />{e.completeness.priority_count}</span>}
                </div>
              )}
            </motion.button>
          ))}
        </div>
      )}
      {creating && <EntityFormDialog open onOpenChange={setCreating} uid={universe.id} defaultCategory={cat || "Character"} onSaved={(d) => navigate(`${base}/lore/${d.id}`)} />}
      {sendingBulk && <BulkSendDialog uid={universe.id} onOpenChange={setSendingBulk} />}
      {merging && <MergeDialog open onOpenChange={setMerging} uid={universe.id} onMerged={(id) => navigate(`${base}/lore/${id}`)} />}
      {templating && <TemplateManager uid={universe.id} onOpenChange={setTemplating} />}
      {managing && <CategoryManager open onOpenChange={setManaging} uid={universe.id} />}
    </div>
  );
}
